/**
 * Linha do tempo = histórico completo (auditoria) da fila (ADR-0069, D7).
 * Agrupa por dia, mais recente primeiro; chips por tipo de evento; no dia
 * de hoje mostra também as requisições cujo prazo vence hoje.
 */
import React, { useMemo } from "react";
import {
  FiCheck, FiChevronRight, FiClock, FiDollarSign, FiFileText, FiMessageSquare, FiPaperclip,
  FiPlus, FiRefreshCw, FiTruck, FiUser,
} from "react-icons/fi";
import styled from "styled-components";

import { SITUACAO_LABEL, SITUACAO_TONE, TIPOS_EVENTO, TIPO_EVENTO_LABEL, TIPO_EVENTO_TONE } from "../../../services/requisicoesCompraUi";
import { getToneTokens } from "../../../styles/shared";
import { Chip, Mono, Pill, Tag } from "../_shared/quadro/styles";
import { Avatar, PrazoCell, ProgressoItens, StatusPill, TagOrigem } from "./ui";

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: var(--shadow-1);
  overflow: hidden;
`;

const Cabeca = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: var(--surface-2);
  border-bottom: 1px solid var(--border);
  > .info { margin-left: auto; font-size: 12px; color: var(--ink-4); white-space: nowrap; }
`;

const Dia = styled.div`
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr);
  border-bottom: 1px solid var(--border-subtle);
  &:last-child { border-bottom: 0; }
  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const DiaCol = styled.div`
  padding: 14px 16px;
  border-right: 1px solid var(--border-subtle);
  background: ${({ $hoje }) => ($hoje ? "var(--accent-soft)" : "transparent")};
  > .dow { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; color: ${({ $hoje }) => ($hoje ? "var(--accent)" : "var(--ink-3)")}; }
  > .dia { font-size: 22px; font-weight: 700; line-height: 1.2; }
  > .mes { font-size: 12px; color: var(--ink-3); }
  @media (max-width: 720px) { border-right: 0; border-bottom: 1px solid var(--border-subtle); }
`;

const Evento = styled.button`
  display: grid;
  grid-template-columns: 52px 28px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  width: 100%;
  padding: 9px 16px;
  border: none;
  border-bottom: 1px solid var(--border-subtle);
  background: transparent;
  color: var(--ink);
  text-align: left;
  font: inherit;
  cursor: pointer;
  &:last-child { border-bottom: 0; }
  &:hover { background: var(--surface-hover); }
  > .hora { font-family: var(--font-mono); font-size: 11.5px; color: var(--ink-4); }
  > .texto { min-width: 0; }
  > .texto .l1 { font-size: 13px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  > .texto .l2 { font-size: 12px; color: var(--ink-2); display: flex; align-items: center; gap: 6px; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const Icone = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${({ $tone }) => { const t = getToneTokens(null, $tone); return `background: ${t.background}; color: ${t.text};`; }}
`;

const Faixa = styled.div`
  padding: 8px 16px;
  background: var(--surface-2);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: var(--ink-3);
  border-top: 1px solid var(--border-subtle);
`;

const LinhaRica = styled.button`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.2fr) auto minmax(120px, 1fr) auto auto 28px 16px;
  gap: 12px;
  align-items: center;
  width: 100%;
  padding: 10px 16px;
  border: none;
  border-top: 1px solid var(--border-subtle);
  background: transparent;
  color: var(--ink);
  text-align: left;
  font: inherit;
  cursor: pointer;
  &:hover { background: var(--surface-hover); }
  > div { min-width: 0; }
  @media (max-width: 900px) { grid-template-columns: minmax(0, 1fr) auto auto; > .esconde { display: none; } }
`;

const ICONE_POR_TIPO = {
  status: FiRefreshCw, item: FiCheck, comentario: FiMessageSquare, nova: FiPlus,
  cotacao: FiDollarSign, oc: FiTruck, anexo: FiPaperclip, comprador: FiUser, prioridade: FiClock,
};

function chaveDia(d) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function hora(iso) {
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function PillMini({ situacao }) {
  return (
    <Pill $tone={SITUACAO_TONE[situacao] || "neutral"} style={{ height: 20, fontSize: 11, padding: "0 8px" }}>
      {SITUACAO_LABEL[situacao] || situacao}
    </Pill>
  );
}

export default function LinhaEventos({ eventos, vencemHoje = [], onAbrir, tipo, onTipo, carregando }) {
  const contagem = useMemo(() => {
    const c = {};
    (eventos || []).forEach((e) => { c[e.tipo] = (c[e.tipo] || 0) + 1; });
    return c;
  }, [eventos]);

  const grupos = useMemo(() => {
    const hojeK = chaveDia(new Date());
    const map = new Map();
    (eventos || []).forEach((e) => {
      const d = new Date(e.timestamp);
      const k = chaveDia(d);
      if (!map.has(k)) map.set(k, { data: d, hoje: k === hojeK, itens: [] });
      map.get(k).itens.push(e);
    });
    if (vencemHoje.length && !map.has(hojeK)) {
      map.set(hojeK, { data: new Date(), hoje: true, itens: [] });
    }
    return Array.from(map.values()).sort((a, b) => b.data - a.data);
  }, [eventos, vencemHoje]);

  const filtrados = (g) => g.itens.filter((e) => !tipo || e.tipo === tipo);

  return (
    <Card>
      <Cabeca>
        <Chip type="button" $on={!tipo} onClick={() => onTipo(null)}>
          Todos <span className="n">{(eventos || []).length}</span>
        </Chip>
        {TIPOS_EVENTO.filter((t) => contagem[t.value]).map((t) => (
          <Chip key={t.value} type="button" $on={tipo === t.value} $cor={getToneTokens(null, t.tone).accent} onClick={() => onTipo(tipo === t.value ? null : t.value)}>
            <span className="dot" /> {t.label} <span className="n">{contagem[t.value]}</span>
          </Chip>
        ))}
        <span className="info">
          Histórico completo · {(eventos || []).length} eventos{carregando ? " · atualizando…" : ""}
        </span>
      </Cabeca>

      {grupos.length === 0 ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--ink-3)", fontSize: 13 }}>
          Nenhum evento no período.
        </div>
      ) : grupos.map((g) => {
        const lista = filtrados(g);
        if (!lista.length && !(g.hoje && vencemHoje.length)) return null;
        return (
          <Dia key={g.data.toISOString()}>
            <DiaCol $hoje={g.hoje}>
              <div className="dow">{g.hoje ? "Hoje" : g.data.toLocaleDateString("pt-BR", { weekday: "long" })}</div>
              <div className="dia">{g.data.getDate()}</div>
              <div className="mes">{g.data.toLocaleDateString("pt-BR", { month: "long" })} · {lista.length} eventos</div>
            </DiaCol>
            <div style={{ minWidth: 0 }}>
              {lista.map((e) => {
                const Ico = ICONE_POR_TIPO[e.tipo] || FiFileText;
                return (
                  <Evento key={e.id} type="button" onClick={() => onAbrir(e.requisicao_id)}>
                    <span className="hora">{hora(e.timestamp)}</span>
                    <Icone $tone={TIPO_EVENTO_TONE[e.tipo] || "neutral"}><Ico size={14} /></Icone>
                    <div className="texto">
                      <div className="l1">
                        <strong>{e.ator_nome || "Sistema"}</strong>
                        {e.de && e.para ? (
                          <>
                            <span style={{ color: "var(--ink-3)" }}>moveu</span>
                            <PillMini situacao={e.de} /> <span style={{ color: "var(--ink-4)" }}>→</span> <PillMini situacao={e.para} />
                          </>
                        ) : (
                          <span style={{ color: "var(--ink-2)" }}>{e.detalhe}</span>
                        )}
                      </div>
                      <div className="l2">
                        <Mono $size={11} style={{ color: "var(--accent)" }}>{e.requisicao_numero}</Mono>
                        {e.cliente_nome ? <span>· {e.cliente_nome}</span> : null}
                        {e.de && e.para && e.detalhe ? <span>· {e.detalhe}</span> : null}
                      </div>
                    </div>
                    <Tag $tone="neutral" $h={22} style={{ background: "var(--surface-2)", color: "var(--ink-3)" }}>
                      {TIPO_EVENTO_LABEL[e.tipo] || e.tipo}
                    </Tag>
                  </Evento>
                );
              })}
              {g.hoje && vencemHoje.length ? (
                <>
                  <Faixa>Prazos vencendo neste dia</Faixa>
                  {vencemHoje.map((r) => (
                    <LinhaRica key={r.id} type="button" onClick={() => onAbrir(r.id)}>
                      <div>
                        <Mono>{r.numero}</Mono>
                        <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.cliente_nome || "Sem cliente"}</div>
                      </div>
                      <div className="esconde" style={{ fontSize: 12, color: "var(--ink-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.projeto_resumo || "—"}</div>
                      <div className="esconde"><TagOrigem origem={r.origem} label={r.origem_label} curta /></div>
                      <div className="esconde"><ProgressoItens req={r} /></div>
                      <div><StatusPill situacao={r.situacao} /></div>
                      <div className="esconde"><PrazoCell req={r} /></div>
                      <div className="esconde"><Avatar nome={r.comprador_nome} size={24} /></div>
                      <FiChevronRight size={14} style={{ color: "var(--ink-4)" }} />
                    </LinhaRica>
                  ))}
                </>
              ) : null}
            </div>
          </Dia>
        );
      })}
    </Card>
  );
}
